import { useEffect } from 'react';

import { StepperContainer, StepperProvider } from '../../DashboardCustomer/OnboardingOffshore/StepperContainer';
import { useStepperContext } from '../../../components/Stepper/Context';
import { useAuth } from '../../../store/auth';

import { CompanyTransferData } from './CompanyTransferData';
import { DirectorsPage } from './DirectorsPage';
import { ShareholdersPage } from './ShareholdersPage';
import { SummaryPage } from './SummaryPage';
import {
  clearTransferStepSession,
  MID_TRANSFER_PROGRESS_STEPS,
  TRANSFER_STEP_PENDING_KEY,
  TRANSFER_STEP_SESSION_KEY
} from './constants';

const STEPS = [
  { id: 'objectives', order: 2 },
  { id: 'transfer_directors', order: 3 },
  { id: 'transfer_shareholders', order: 4 },
  { id: 'summary', order: 5 }
];

const STEP_IDS = STEPS.map((step) => step.id);

const isMidTransferProgress = (progressStep?: string | null) =>
  !!progressStep && (MID_TRANSFER_PROGRESS_STEPS as readonly string[]).includes(progressStep);

/**
 * Restaura step da sessionStorage apenas quando:
 * 1) PENDING_KEY existe (mesmo fluxo pós-create + remount do AuthGuard), ou
 * 2) progress_step do backend indica transfer em andamento, ou
 * 3) progress_step === 'summary' E já há sessão de transfer neste tab
 *    (summary é compartilhado com onboarding de nova offshore — sem sessão, não retomar).
 * Caso contrário descarta storage stale (ex.: usuário voltou em "objectives" / nova transfer).
 */
const getInitialStep = (progressStep?: string | null) => {
  const pendingStep = sessionStorage.getItem(TRANSFER_STEP_PENDING_KEY);
  if (pendingStep && STEP_IDS.includes(pendingStep)) {
    sessionStorage.removeItem(TRANSFER_STEP_PENDING_KEY);
    sessionStorage.setItem(TRANSFER_STEP_SESSION_KEY, pendingStep);
    return pendingStep;
  }

  const storedStep = sessionStorage.getItem(TRANSFER_STEP_SESSION_KEY);

  if (isMidTransferProgress(progressStep)) {
    if (storedStep && STEP_IDS.includes(storedStep)) {
      return storedStep;
    }
    if (progressStep && STEP_IDS.includes(progressStep)) {
      return progressStep;
    }
  }

  // Transfer checkout: backend usa o mesmo "summary" da nova offshore.
  if (progressStep === 'summary' && storedStep && STEP_IDS.includes(storedStep)) {
    return storedStep;
  }

  if (storedStep) {
    clearTransferStepSession();
  }

  return 'objectives';
};

// Criar/trocar a empresa (registerCompanyTransfer -> changeCompanySelected) troca o token no
// meio do wizard. Trocar o token dispara AuthGuard.isLoading, que desmonta e remonta toda a
// rota enquanto refaz o /me — o que apaga o estado local do StepperProvider. Persistimos o
// step atual na sessionStorage para sobreviver a esse remonte e não voltar pro passo 1.
const StepPersistence = () => {
  const stepperContext = useStepperContext();

  useEffect(() => {
    if (stepperContext.currentStep) {
      sessionStorage.setItem(TRANSFER_STEP_SESSION_KEY, stepperContext.currentStep.id);
    }
  }, [stepperContext.currentStep]);

  return null;
};

export const CompanyTransferProvider = () => {
  const { user } = useAuth();
  const progressStep = user?.account?.progress_step;
  const initialStep = getInitialStep(progressStep);

  return (
    <StepperProvider steps={STEPS} initialStep={initialStep}>
      <StepPersistence />
      <StepperContainer step="objectives" component={CompanyTransferData} />
      <StepperContainer step="transfer_directors" component={DirectorsPage} />
      <StepperContainer step="transfer_shareholders" component={ShareholdersPage} />
      <StepperContainer step="summary" component={SummaryPage} />
    </StepperProvider>
  );
};
